import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import axios from 'axios';
import i18n from '../i18n';

const SettingsContext = createContext(null);

// Currency codes for English display
const CURRENCY_CODES = {
  'SAR': 'SAR',
  'USD': 'USD',
  'EUR': 'EUR',
  'GBP': 'GBP',
  'AED': 'AED',
  'KWD': 'KWD',
  'QAR': 'QAR',
  'BHD': 'BHD',
  'OMR': 'OMR',
  'EGP': 'EGP',
  'INR': 'INR',
  'PKR': 'PKR',
  'CNY': 'CNY',
  'JPY': 'JPY',
  'TRY': 'TRY',
  'JOD': 'JOD',
  'LBP': 'LBP',
};

// Currency symbols for Arabic display
// Note: SAR uses official Saudi Riyal symbol (rendered via SARSymbol component)
const CURRENCY_SYMBOLS_AR = {
  'SAR': 'SAR', // Will be rendered as SVG icon via SARSymbol component
  'USD': '$',
  'EUR': '€',
  'GBP': '£',
  'AED': 'د.إ',
  'KWD': 'د.ك',
  'QAR': 'ر.ق',
  'BHD': 'د.ب',
  'OMR': 'ر.ع',
  'EGP': 'ج.م',
  'INR': '₹',
  'PKR': 'Rs',
  'CNY': '¥',
  'JPY': '¥',
  'TRY': '₺',
  'JOD': 'د.أ',
  'LBP': 'ل.ل',
};

// Default settings
const DEFAULT_SETTINGS = {
  company_name: '',
  company_name_ar: '',
  industry: 'technology',
  region: 'SA',
  currency: 'SAR',
  currency_symbol: 'SAR',
  tax_rate: 15,
  tax_name: 'VAT',
  tax_name_ar: 'ضريبة القيمة المضافة',
  language: 'en',
  document_language: 'bilingual_en',
  timezone: 'Asia/Riyadh',
  address: '',
  address_ar: '',
  city: '',
  city_ar: '',
  postal_code: '',
  phone: '',
  email: '',
  website: '',
  tax_number: '',
  cr_number: '',
  logo_url: '',
  terms_conditions: '',
  terms_conditions_ar: '',
};

export const SettingsProvider = ({ children }) => {
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchSettings = useCallback(async () => {
    try {
      setLoading(true);
      // Use public endpoint (no auth required) for settings
      const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/settings/company/public`);
      if (res.data) {
        setSettings(prev => ({ ...prev, ...res.data }));
        
        // Sync language with i18n if settings have a language preference
        if (res.data.language) {
          const currentLang = i18n.language;
          // Only update if different from current to avoid loops
          if (currentLang !== res.data.language) {
            // Change i18n language
            i18n.changeLanguage(res.data.language);
            localStorage.setItem('i18nextLng', res.data.language);
            // Update document direction
            document.documentElement.dir = res.data.language === 'ar' ? 'rtl' : 'ltr';
            document.documentElement.lang = res.data.language;
            if (res.data.language === 'ar') {
              document.documentElement.classList.add('font-arabic');
            } else {
              document.documentElement.classList.remove('font-arabic');
            }
          }
        }
      }
    } catch (err) {
      console.error('Failed to fetch company settings:', err);
      setError(err.message);
      // Use defaults on error
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  // Update settings and refetch
  const updateSettings = async (newSettings) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/settings/company`,
        newSettings,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Refetch to get updated settings with auto-calculated fields
      await fetchSettings();
      return true;
    } catch (err) {
      console.error('Failed to update company settings:', err);
      throw err;
    }
  };

  // Helper function to format currency
  const formatCurrency = useCallback((amount, showCode = false) => {
    if (amount === null || amount === undefined) return `${settings.currency_symbol} 0.00`;
    
    const formattedAmount = Number(amount).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
    
    if (showCode) {
      return `${settings.currency} ${formattedAmount}`;
    }
    return `${settings.currency_symbol} ${formattedAmount}`;
  }, [settings.currency, settings.currency_symbol]);

  // Get currency display based on language
  // English: codes (SAR, AED, USD)
  // Arabic: symbols (ر.س, د.إ, $)
  const getCurrencyDisplay = useCallback((currencyCode, lang = null) => {
    const language = lang || settings.language;
    if (language === 'ar') {
      return CURRENCY_SYMBOLS_AR[currencyCode] || currencyCode;
    }
    return CURRENCY_CODES[currencyCode] || currencyCode;
  }, [settings.language]);

  // Get currency symbol from code (legacy - returns language-aware display)
  const getCurrencySymbol = useCallback((currencyCode) => {
    return getCurrencyDisplay(currencyCode);
  }, [getCurrencyDisplay]);

  // Calculate tax amount
  const calculateTax = useCallback((amount) => {
    if (!amount || settings.tax_name === 'None') return 0;
    return (amount * settings.tax_rate) / 100;
  }, [settings.tax_rate, settings.tax_name]);

  // Get language-aware currency symbol for current settings
  const getActiveCurrencyDisplay = useCallback(() => {
    return getCurrencyDisplay(settings.currency, settings.language);
  }, [settings.currency, settings.language, getCurrencyDisplay]);

  // Check if current currency is SAR (for rendering official symbol)
  const isSAR = settings.currency === 'SAR';

  const value = {
    settings,
    loading,
    error,
    updateSettings,
    refetchSettings: fetchSettings,
    formatCurrency,
    getCurrencySymbol,
    getCurrencyDisplay,
    calculateTax,
    // Direct access to commonly used values - language aware
    currencySymbol: getCurrencyDisplay(settings.currency, settings.language),
    currencyCode: settings.currency,
    taxRate: settings.tax_rate,
    taxName: settings.tax_name,
    companyName: settings.company_name,
    companyNameAr: settings.company_name_ar,
    region: settings.region,
    language: settings.language,
    // Helper to check if SAR for rendering official symbol
    isSAR,
  };

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
};

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
};

// Export currency mappings for use in other components
export { CURRENCY_CODES, CURRENCY_SYMBOLS_AR, DEFAULT_SETTINGS };
